<template>
  <div class="seat-selection-page" v-if="Object.keys(booking_data).length">
    <section v-if="loading" class="section section--loader">
      <loader size="large" :show_timing_slot="true" />
    </section>
    <!-- additional-seats stays mounted via v-show so the page can
         delegate rybbit tracking (track_event) to it via $refs even
         while the seatmap is still loading or the page is about to
         redirect on a fetch failure. -->
    <div v-show="!loading">
      <b-container class="my-4">
        <b-row>
          <b-col class="ml-3">
            <button-back :forwards_only="forwards_only" />
          </b-col>
        </b-row>
      </b-container>
      <b-container>
        <b-row>
          <b-col sm="12" md="12" lg="8" xl="9">
            <additional-seats
              ref="additional_seats"
              :flight_offer="flight_offer"
              :seatmap="seatmap_envelope"
              :travelers="traveler_details"
              :value="selections"
              @input="selections = $event"
              @status="update_advance_status"
            />
          </b-col>
          <b-col sm="12" md="12" lg="4" xl="3">
            <div class="sidebar sticky-top">
              <div class="detail--panel detail--panel-sidebar pt-0" compact>
                <sidebar-resume :flight_offers="[flight_offer]" />
                <btn-checkout
                  id="btn-checkout-sidebar"
                  :copy="advance_label"
                  :conditions="advance_disabled"
                  :popover="advance_popover"
                  @handle_data="handle_next_step()"
                />
                <p
                  v-if="credit_insufficient"
                  v-b-popover.hover.bottom="
                    'Il tuo credito welfare non è sufficiente ad acquistare questa soluzione'
                  "
                  class="credit-warning text-center text-danger mt-2 mb-1"
                >
                  <vb-icon
                    name="info"
                    class="text-danger"
                    :color="sass('danger')"
                    :size="16"
                  />
                  Credito insufficiente
                </p>
                <p
                  v-else-if="advance_status_disabled"
                  class="credit-warning text-center text-info mt-2 mb-1"
                >
                  Seleziona tutti i posti di ogni passeggero prima di continuare.
                </p>
              </div>
            </div>
          </b-col>
        </b-row>
      </b-container>
    </div>
  </div>
</template>

<script>
// Thin wrapper around <additional-seats>. The picker UI, selection
// rules, fail modal, hydration, and rybbit tracking live in the child;
// this view owns route guard, seatmap fetch + redirects, apply_seats
// reprice + Vuex commit, and the sidebar advance button.
// Submit-flow + unavailable + mandatory-impossible rybbit events are
// proxied through $refs.additional_seats so the slim_* payload mapping
// stays in one place.

import { get_config } from "@/utilities/";
import { get_availability } from "@/utilities/commons";
import {
  get_seatmap,
  apply_seats,
  validate_seats_priced,
  seats_are_mandatory,
  has_capability_for,
} from "@liveflight/providers";
import { get_markup } from "@liveflight/utilities/price";
import { get_flight_order_body } from "@liveflight/providers/amadeus/checkout";
import { is_infant } from "@liveflight/utilities/travelers";
import SidebarResume from "@liveflight/components/blocks/resume/resume";
import BtnCheckout from "@liveflight/components/atoms/btn-checkout";
import Loader from "@components/blocks/loader";
import AdditionalSeats from "@liveflight/components/blocks/ancillaries/additional-seats.vue";

export default {
  name: "seat-selection",
  components: {
    "sidebar-resume": SidebarResume,
    "btn-checkout": BtnCheckout,
    "additional-seats": AdditionalSeats,
    loader: Loader,
  },
  data() {
    return {
      loading: true,
      reload_seat_map: false,
      seatmap_envelope: {},
      selections: {},
      // Pushed up via @status from <seat-map>: { isEditing, btnText,
      // btnDisabled }. Drives the per-segment "Continua / Conferma"
      // cycle on the sidebar advance button.
      advance_label: "",
      advance_status_disabled: false,
      advance_is_editing: true,
      checkout_btn_popover:
        "Prima devi compilare i riferimenti di contatto, i dettagli dei viaggiatori e accettare le condizioni d'uso in questa pagina.",
    };
  },
  computed: {
    booking_data: {
      get() {
        return this.$store.state.booking_data;
      },
      set(value) {
        this.$store.commit("booking_data", value);
      },
    },
    flight_offer: {
      get() {
        return this.$store.state.liveflight.flight_offer;
      },
      set(value) {
        this.$store.commit("liveflight/flight_offer", value);
      },
    },
    traveler_details: {
      get() {
        return this.$store.state.liveflight.traveler_details;
      },
      set(value) {
        this.$store.commit("liveflight/traveler_details", value);
      },
    },
    contact_details: {
      get() {
        return this.$store.state.liveflight.contact_details;
      },
      set(value) {
        this.$store.commit("liveflight/contact_details", value);
      },
    },
    must_select_seats() {
      return seats_are_mandatory(this.flight_offer);
    },
    forwards_only() {
      return has_capability_for(this.flight_offer, "forwardsOnly");
    },
    credit_insufficient() {
      return get_availability(get_markup(this.flight_offer).mu_price)
        .show_credit_alert;
    },
    advance_disabled() {
      return this.advance_status_disabled || this.credit_insufficient;
    },
    advance_popover() {
      return this.credit_insufficient ? "" : this.checkout_btn_popover;
    },
  },
  created() {
    if (!Object.keys(this.booking_data).length) {
      this.$router.replace("detail");
    }
  },
  mounted() {
    this.fetch_seat_map();
  },
  watch: {
    reload_seat_map() {
      this.$nextTick(() => this.fetch_seat_map());
    },
  },
  methods: {
    /**
     * additional-seats holds the rybbit slim_* mapping; proxy
     * submit-flow + fetch-failure events through it so the payload
     * shape stays consistent with in-picker selection events.
     */
    track({ event, data }) {
      const child = this.$refs.additional_seats;
      if (!child) return;
      child.track_event({ event, data });
    },
    fetch_seat_map() {
      const { travelerPricings = [] } = this.flight_offer || {};
      const are_infants_present = travelerPricings.some(is_infant);
      const allow_malformed = get_config(
        "guiSettings.customizations.allowMalformedDecks",
        false,
      );
      get_seatmap(this.flight_offer, {
        areInfantsPresent: are_infants_present,
        allowMalformed: allow_malformed,
      })
        .then((response) => {
          // Provider resolves to null when seat selection isn't supported.
          if (!response) {
            this.track({
              event: "seat-map:unavailable",
              data: {
                type: "info",
                reason: "Provider does not support seat selection",
              },
            });
            return;
          }
          if (this.must_select_seats) {
            const error_message =
              "Questa tariffa ha un problema con la mappa dei posti. Per proseguire, ti consigliamo di selezionarne un'altra.";
            const reason = this.diagnose_response_for_mandatory(response);
            if (reason) {
              this.track({
                event: "seat-map:mandatory-selection-impossible",
                data: { type: "error", message: error_message, reason },
              });
              this.exit_with_global_modal(error_message, () =>
                this.$router.replace("detail"),
              );
              return;
            }
          }
          // Optional seatmap missing/incomplete → skip to checkout.
          if (response.empty) {
            this.track({
              event: "seat-map:unavailable",
              data: { type: "info", reason: "Missing seat map" },
            });
            this.$router.replace("checkout");
            return;
          }
          if (!response.isValid) {
            this.track({
              event: "seat-map:unavailable",
              data: { type: "info", reason: "Incomplete traveler pricing" },
            });
            this.$router.replace("checkout");
            return;
          }
          const all_disabled = Object.values(response.mappedDecks || {}).every(
            (deck) => deck.segment.disabled,
          );
          if (all_disabled) {
            this.track({
              event: "seat-map:unavailable",
              data: { type: "info", reason: "All decks are disabled" },
            });
            this.$router.replace("checkout");
            return;
          }
          this.seatmap_envelope = response;
        })
        .catch(() => {
          if (this.must_select_seats) {
            this.track({
              event: "seat-map:mandatory-selection-impossible",
              data: { type: "error", reason: "Error from APIs" },
            });
            this.$router.replace("detail");
            return;
          }
          this.$router.replace("checkout");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    /**
     * First failing reason (or '') for must-select-seats. Pulled out so
     * fetch_seat_map reads top-to-bottom without nested if/return chains.
     */
    diagnose_response_for_mandatory(response) {
      if (response.empty) return "Missing seat map data";
      if (!response.isValid) return "Incomplete traveler pricing";
      if (!response.canAllPassengersSelectOneSeat) {
        return "Some travelers cannot choose a seat in some decks";
      }
      const any_disabled = Object.values(response.mappedDecks || {}).some(
        (deck) => deck.segment.disabled,
      );
      if (any_disabled) return "Some decks are disabled";
      return "";
    },
    update_advance_status({ isEditing, btnText, btnDisabled }) {
      this.advance_is_editing = isEditing;
      this.advance_label = btnText;
      this.advance_status_disabled = btnDisabled;
    },
    handle_next_step() {
      if (this.advance_is_editing) {
        window.eventBus.$emit("seat_map_next_step");
        return;
      }
      this.handle_confirm_seats();
    },
    has_seats_selected() {
      return Object.values(this.selections).some(
        (segment_selections) => Object.keys(segment_selections).length > 0,
      );
    },
    handle_confirm_seats() {
      if (!this.has_seats_selected()) {
        this.track({
          event: "seat-map:submit:no-seats",
          data: { type: "info" },
        });
        this.$router.push("checkout");
        return;
      }
      this.loading = true;
      this.scroll_top();
      // Provider owns the seat-apply + reprice; returns a normalized
      // FlightOffer, never a raw upstream envelope.
      apply_seats(this.flight_offer, this.selections)
        .then((priced_offer) => {
          let seats_priced_correctly = false;
          try {
            if (!priced_offer) return;
            this.flight_offer = priced_offer;
            seats_priced_correctly = validate_seats_priced(
              this.flight_offer,
              this.selections,
              priced_offer,
            );
            this.booking_data = {
              ...this.booking_data,
              live_price: get_markup(priced_offer).mu_price,
              flightCreateOrderBody: get_flight_order_body({
                flight_offers_pricing: [priced_offer],
                traveler_details: this.traveler_details,
                contact_details: this.contact_details,
              }),
            };
          } catch (e) {
            this.track({
              event: "seat-map:submit:fail",
              data: { type: "error", message: e.message, reason: "Exception" },
            });
            this.log("error handle_confirm_seats");
            this.log(e);
          } finally {
            if (!seats_priced_correctly) {
              const error_message =
                "Attenzione! Alcuni posti scelti potrebbero non essere più disponibili.";
              this.reload_seat_map = !this.reload_seat_map;
              this.track({
                event: "seat-map:submit:fail",
                data: {
                  type: this.must_select_seats ? "error" : "warning",
                  message: error_message,
                  reason: "Mismatch in API Response",
                },
              });
              if (this.must_select_seats) {
                this.exit_with_global_modal(error_message);
              } else {
                window.eventBus.$emit("got_toast_notification", {
                  text: error_message,
                  variant: "warning",
                });
                this.$router.push("checkout");
              }
            } else {
              this.track({
                event: "seat-map:submit:success",
                data: { type: "info" },
              });
              this.$router.push("checkout");
            }
          }
        })
        .catch((e) => {
          this.track({
            event: "seat-map:submit:fail",
            data: {
              type: "error",
              message: e.message,
              reason: "Error from APIs",
            },
          });
          this.exit_with_global_modal(e.message, e.status);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    exit_with_global_modal(message, callback = function () {}, status = 200) {
      window.eventBus.$emit("got_modal_notification", {
        msg: message,
        status,
        callback,
      });
    },
  },
};
</script>

<style lang="scss">
.seat-selection-page {
  padding-bottom: $grid-gutter-width;

  label {
    font-size: small;
  }

  .credit-warning {
    font-size: $font-size-base;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: $grid-gutter-width / 6;
  }
}
</style>
